import { useRef, useState } from 'react';
import { X } from 'lucide-react';

export interface ColorValue {
  hex: string;
  label?: string;
}

export interface ColorChipProps {
  value: ColorValue;
  onChange: (next: ColorValue) => void;
  onRemove: () => void;
}

export function ColorChip({ value, onChange, onRemove }: ColorChipProps) {
  const ref = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);

  return (
    <div className="flex items-center gap-2 pl-1.5 pr-2 py-1 border border-border rounded-lg bg-card">
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className="w-6 h-6 rounded-md border border-black/10 cursor-pointer flex-shrink-0"
        style={{ background: value.hex }}
        aria-label="Open color picker"
      >
        <input
          ref={ref}
          type="color"
          value={value.hex}
          onChange={(e) => onChange({ ...value, hex: e.target.value })}
          className="invisible w-0 h-0"
        />
      </button>
      <span className="font-mono text-[12px] text-muted-foreground uppercase">{value.hex}</span>
      {editing ? (
        <input
          autoFocus
          value={value.label || ''}
          onChange={(e) => onChange({ ...value, label: e.target.value })}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') setEditing(false);
          }}
          placeholder="Name…"
          className="border-none outline-none text-[12.5px] w-20 bg-transparent"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={`bg-transparent border-none text-[12.5px] cursor-text ${
            value.label ? 'text-foreground' : 'text-muted-foreground'
          }`}
        >
          {value.label || 'name…'}
        </button>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="w-5 h-5 grid place-items-center border-none bg-transparent text-muted-foreground hover:text-foreground cursor-pointer rounded"
      >
        <X className="size-3" />
      </button>
    </div>
  );
}
